import { z } from 'zod'

import {
  CuratedListItemActionType,
  CuratedListItemType,
} from '../constants/curatedList'
import { feedDeeplinkSchema } from './feed'
import { listQueryZodFields } from './system'
import { dbEntitySchema } from './system'

const optionalDateInputSchema = z
  .union([z.date(), z.coerce.date(), z.number(), z.string()])
  .optional()
  .nullable()

export const curatedListItemSchema = dbEntitySchema.extend({
  curatedList: z.string(),
  enable: z.boolean(),
  enableStart: z.date().nullish(),
  enableEnd: z.date().nullish(),
  sort: z.number(),
  type: z.nativeEnum(CuratedListItemType),
  venue: z.string().or(z.record(z.unknown())).nullish(),
  event: z.string().or(z.record(z.unknown())).nullish(),
  link: feedDeeplinkSchema.nullish(),
  date: z.date().nullish(),
  contentSeoPathname: z.string().nullish(),
  title: z.string().nullish(),
  description: z.string().nullish(),
  imageUrl: z.string().nullish(),
  active: z.boolean().optional(),
})

const curatedListItemCreateArgsBaseSchema = z.object({
  curatedListId: z.string(),
  venueId: z.string(),
  eventId: z.string().optional().nullable(),
  title: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  imageUrl: z.string().url().optional().nullable(),
  enableStart: optionalDateInputSchema,
  enableEnd: optionalDateInputSchema,
  enable: z.boolean().optional(),
})

function refineDateRange<
  T extends { enableStart?: unknown; enableEnd?: unknown },
>(schema: z.ZodType<T>) {
  return schema.refine(
    (value) => {
      if (!value.enableStart || !value.enableEnd) return true
      const start = value.enableStart as Date | string | number
      const end = value.enableEnd as Date | string | number
      return new Date(start) < new Date(end)
    },
    {
      path: ['enableStart'],
      message: 'enableStart must be before enableEnd',
    }
  )
}

export const curatedListItemCreateArgsSchema = refineDateRange(
  curatedListItemCreateArgsBaseSchema
)

export const curatedListItemUpdateByIdArgsSchema = refineDateRange(
  curatedListItemCreateArgsBaseSchema
    .omit({
      curatedListId: true,
      venueId: true,
      eventId: true,
    })
    .extend({
      title: z.string().optional(),
      description: z.string().optional(),
      imageUrl: z.string().url().optional(),
      type: z.nativeEnum(CuratedListItemType).optional(),
    })
    .partial()
)

export const curatedListItemActionUpdateSortSchema = z.object({
  type: z.literal(CuratedListItemActionType.UPDATE_SORT),
  sort: z.number(),
})

export const curatedListItemListQuerySchema = z.object({
  ...listQueryZodFields,
  type: z.nativeEnum(CuratedListItemType).optional().nullable(),
  active: z.string().optional().nullable(),
})

export const curatedListItemListParamsSchema = z.object({
  id: z.string(),
})

export const curatedListItemByIdParamsSchema = z.object({
  id: z.string(),
  itemId: z.string(),
})

export const curatedListItemCreateByCuratedListIdArgsSchema = refineDateRange(
  curatedListItemCreateArgsBaseSchema.omit({
    curatedListId: true,
  })
)

export const curatedListItemListResponseSchema = z.object({
  data: z.array(curatedListItemSchema),
  count: z.number(),
})
